import { each, sortBy } from 'lodash-es';

import * as angular from 'angular';

import * as offers from './../api/offers.service';

/**
 * This service provides methods to turn microchart data (from /api/offers/{offerId}/microchart) into highcharts graph
 *
 * This code was pulled from the previous microcharts services and updated to use tiers rather than market share terms
 *
 * As of right now, we have two methods you can call:
 * - generateMicroChart
 * - generateMacroChart
 *
 * These both call a number of other methods that are badly named to create the highcharts object
 *
 * Ideally, we would just pass the microchart data to a filter to create the series properties,
 * then set that on a highcharts graph object that is already created, based on the size, etc.
 */
export default class TierMicroChartService {
  bigGraphInfo: any = {
    chart: {
      // height: 140,
      // width: 110,
      type: 'columnrange',
      plotBackgroundColor: '#ffffff',
      borderWidth: 0,
      plotBorderWidth: 1,
      marginTop: 6,
      showAxes: true
    },
    colors: [
      '#c77329',
      '#8ac342',
      {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, 'rgba(255,255,255,.002)'],
          [0.05, 'rgba(255,255,255,.002)'],
          [0.05, '#8ac342'],
          [1, '#8ac342']
        ]
      }
    ],
    plotOptions: {
      columnrange: {
        borderWidth: 2,
        pointWidth: 14,
        grouping: false,
        groupPadding: 0,
        minPointLength: 4,
        enableMouseTracking: false,
        zIndex: 3,
        states: {
          hover: {
            enabled: false
          }
        },
        events: {
          // Kludge to fix an apparent highcharts bug - see APT-6970
          mouseOver: function () {
            $('.highcharts-tooltip').css('visibility', 'visible');
          },
          mouseOut: function () {
            $('.highcharts-tooltip').css('visibility', 'hidden');
          }
        }
      },
      scatter: {
        zIndex: 5,
        enableMouseTracking: false,
        states: {
          hover: {
            enabled: false
          }
        },
        events: {
          // Kludge to fix an apparent highcharts bug - see APT-6970
          mouseOver: function () {
            $('.highcharts-tooltip').css('visibility', 'visible');
          },
          mouseOut: function () {
            $('.highcharts-tooltip').css('visibility', 'hidden');
          }
        },
        color: 'transparent',
        marker: {
          symbol: 'circle',
          radius: 0
        }
      }
    },
    yAxis: {
      tickAmount: 5,
      labels: {
        enabled: true,
        useHTML: true,
        step: 1,
        formatter: function () {
          if (this.isFirst) {
            return '<span style="color:#8ac342;font-size:12px">Lower</span>';
          } else if (this.isLast) {
            return '<span style="color:#c77329;font-size:12px">Higher</span>';
          } else if (this.value === 0) {
            return null;
          } else {
            return Math.abs(this.value) + '%';
          }
        },
        x: -18
      },
      plotLines: [
        {
          color: '#c0c0c0',
          width: 1,
          value: 0,
          zIndex: 4
        }
      ],
      plotBands: [
        {
          color: '#e0b0b0'
        }
      ],
      gridLineWidth: 0,
      tickWidth: 0,
      title: {
        text: 'Current',
        rotation: 0,
        offset: 50,
        style: {
          fontWeight: 'normal',
          color: '#999999'
        }
      }
    },
    xAxis: {
      labels: {
        enabled: true,
        step: 1,
        useHTML: true,
        // formatter: The formatter function here has been removed in favor of setting it below. The closure created
        // here had no access to the microchart data and thus couldn't use it to display the correct tier names.
        // See below in the generateMacroChart function for it to be connected.
        y: 20
      },
      gridLineWidth: 0,
      tickInterval: 1,
      tickWidth: 0,
      min: 0,
      max: 9,
      plotLines: [
        {
          color: '#4ac6eb',
          width: 2,
          zIndex: 3
        }
      ],
      title: {
        text: 'Market Share',
        style: {
          fontWeight: 'normal',
          color: '#999999'
        },
        margin: 16
      }
    },
    credits: {
      enabled: false
    },
    title: {
      text: null
    },
    legend: {
      enabled: false
    },
    tooltip: {
      enabled: false
    }
  };

  constructor(private highChartDefaults) {
    'ngInject';
  }

  /**
   * Utilizes a set of data to generate the JSON structure needed to render a micro-chart. At the moment the output
   * is a Highcharts config data structure but that may change in the future when we can (hopefully) move to D3.js.
   *
   * @param {offers.MicroChart} microChartData    [description]
   * @param targetMarketShare
   * @param currentMarketShare
   * @returns {*} A Highcharts configuration object.
   */
  generateMicroChart(
    microChartData: offers.MicroChart,
    targetMarketShare?,
    marketShare?
  ) {
    // getSellerToCompareConfig expects to receive the source data for building a micro-chart wrapped in an outer object
    var allocations = {
      targetMarketShare: targetMarketShare,
      marketShare: marketShare
    };

    return this.getSellerToCompareConfig(microChartData, allocations);
  }

  /**
   *
   * @param {offers.MicroChart} microChartData    [description]
   * @param {[type]}                    marketShare       [description]
   * @param {[type]}                    targetMarketShare [description]
   */
  generateMacroChart(
    microChartData: offers.MicroChart,
    targetMarketShare?,
    marketShare?
  ) {
    if (microChartData) {
      let allocations = {
        targetMarketShare: targetMarketShare,
        marketShare: marketShare
      };

      let chartData = this.getSellerToCompareConfig(
        microChartData,
        allocations
      );

      // The only reason for copying the benchmarks data is to fix the similar -> likeMe naming conventions. Sigh.
      // We could probably just pass in the market share directly, but keeping it like this in hopes of reusing the
      // createGraphData from the original service
      let componentData = {
        marketShare: marketShare,
        coverage: {
          something: true
        },
        allocations: allocations
      };

      let bigChartData = this.createGraphData(
        componentData,
        chartData,
        this.pixelShimCalculator(),
        targetMarketShare
      );

      bigChartData.xAxis.labels.formatter = function () {
        if (this.pos !== undefined) {
          let tierInfo = microChartData.tiers.find(
            (tier) => tier.order === this.pos
          );

          return tierInfo
            ? `<span class="chartlabel">${tierInfo.tier.name}</span>`
            : '';
        } else {
          return ``;
        }
      };

      return bigChartData;
    } else {
      return null;
    }
  }

  /**
   * This was formerly in the purchasePlanChartFilters.js and there is still a wrapper function there which calls
   * this one so existing code doesn't have to change, but it has been extracted so it could be reused across many
   * pages which may need to show the larger zoomed version of the micro-chart.
   *
   * This used to be named getSellerToCompareConfig
   *
   * @param microChartData
   * @param allocations
   * @returns {*}
   */
  getSellerToCompareConfig(microChartData: offers.MicroChart, allocations) {
    if (microChartData == null) {
      return false;
    }

    let pricingChartSmallDefaults = this.highChartDefaults
      .pricingChartSmallDefaults;
    let seriesArray: any = [];
    let multiplier = -100;
    let benchmarkAxisOverride: any = {
      yAxis: {}
    };
    let exactPriceX = 0;
    let axisDefault = 11;
    let axisOverride;
    let underAllocated = true;
    let targetMarketShare; // TODO: How do we get this now with tiers?
    let currentMarketShare; // TODO: How do we get this?
    let exactPriceMarkerY;
    let maxYaxisRange =
      microChartData.maxYAxisRange || microChartData.sellerMaxYAxisRange;

    allocations = allocations || {};

    if (allocations.targetMarketShare != null) {
      targetMarketShare = allocations.targetMarketShare / 10;
    }

    if (allocations.marketShare != null) {
      currentMarketShare = allocations.marketShare / 10;
    }

    if (
      microChartData.sellerMaxYAxisRange &&
      microChartData.sellerMaxYAxisRange > 11
    ) {
      axisOverride = {
        yAxis: {
          max: maxYaxisRange * 1.4,
          min: maxYaxisRange * -1.4,
          tickInterval: maxYaxisRange * 2.8,
          plotLines: [
            { color: '#c0c0c0', value: 0, width: 1, zIndex: 2, id: 'zeroline' }
          ]
        }
      };
    } else {
      axisOverride = {
        yAxis: {
          max: axisDefault * 1.4,
          min: axisDefault * -1.4,
          tickInterval: axisDefault * 2.8,
          plotLines: [
            { color: '#c0c0c0', value: 0, width: 1, zIndex: 2, id: 'zeroline' }
          ]
        }
      };
    }

    // logic from processGraphData to parse JSON into series data

    // TODO: We'll need to start ordering the tierData by the order value rather than the threshold down the line.
    // That was added specifically for sorting and ignores the actual values.
    let loSeriesData: any[] = [];
    let hiSeriesData: any[] = [];
    let bestPricingOffer;
    let bestPricingOfferPoint;
    let bestPricingOfferMarker;
    let tierData = sortBy(microChartData.tiers, 'threshold');

    // console.log('Sorted Tier Data:');
    // console.log(tierData);

    let i = 0;

    each(tierData, (tier) => {
      let hiSeries;
      let loSeries;

      // The microcharts service now returns data for all tiers (needed for the impact page)
      // But we only want to draw the tiers that meet this condition
      if (microChartData.sellerCoverage < tier.threshold) {
        return false;
      }

      // console.log(tier.minPct);
      // console.log(tier.maxPct);

      // Assign each data point to the correct series
      if (tier.minPct >= 0 && tier.maxPct > 0) {
        hiSeries = { x: i, low: tier.minPct, high: tier.maxPct };
        loSeries = { x: i, low: null, high: null };
      } else if (tier.minPct < 0 && tier.maxPct <= 0) {
        hiSeries = { x: i, low: null, high: null };
        loSeries = { x: i, low: tier.minPct, high: tier.maxPct };

        // TODO: Where do we get the target market share now?
        if (targetMarketShare === i) {
          loSeries.color = pricingChartSmallDefaults.colors[1];
        }
      } else {
        hiSeries = {
          x: i,
          low: tier.minPct,
          high: tier.maxPct
        };

        loSeries = {
          x: i,
          low: tier.minPct,
          high: 0,
          borderColor: pricingChartSmallDefaults.colors[2]
        };
      }

      if (targetMarketShare === i) {
        exactPriceX = i;
        hiSeries.color = pricingChartSmallDefaults.colors[0];
        loSeries.color = pricingChartSmallDefaults.colors[1];
      }

      // TODO: best offer should be determined in the backend
      // best offer is lowest price at lowest market share
      if (
        typeof bestPricingOffer == 'undefined' ||
        (bestPricingOffer > tier.minPct && tier.minPct)
      ) {
        bestPricingOffer = tier.minPct;
        bestPricingOfferPoint = i;

        if (bestPricingOffer < 0) {
          bestPricingOfferMarker =
            'url(/aptitude/images/bestPriceMarker16x16.png)';
        } else {
          bestPricingOfferMarker =
            'url(/aptitude/images/bestPriceMarker_loss16x16.png)';
        }
      }

      hiSeriesData.push(hiSeries);
      loSeriesData.push(loSeries);

      i++;
    });

    seriesArray.push({
      id: 'hiSeries',
      title: 'HiSeries',
      color: '#ffffff',
      borderColor: pricingChartSmallDefaults.colors[0],
      data: hiSeriesData
    });

    seriesArray.push({
      id: 'loSeries',
      title: 'LoSeries',
      color: '#ffffff',
      borderColor: pricingChartSmallDefaults.colors[1],
      data: loSeriesData
    });

    seriesArray.push({
      id: 'bestOffer',
      title: 'BestOffer',
      type: 'scatter',
      zIndex: 3,
      marker: {
        enabled: true,
        symbol: bestPricingOfferMarker
      },
      data: [[bestPricingOfferPoint, bestPricingOffer]]
    });

    if (
      typeof currentMarketShare !== 'undefined' &&
      typeof targetMarketShare !== 'undefined' &&
      currentMarketShare >= targetMarketShare
    ) {
      underAllocated = false;
    }

    if (typeof currentMarketShare !== 'undefined') {
      benchmarkAxisOverride.xAxis = {
        plotLines: [
          {
            width: 1,
            color: '#4ac6eb',
            value: currentMarketShare,
            zIndex: 1,
            id: 'currentAllocation'
          }
        ]
      };

      if (currentMarketShare > 0) {
        benchmarkAxisOverride.xAxis.plotBands = [
          {
            color: this.allocationColor(underAllocated),
            from: -1,
            to: currentMarketShare,
            id: 'currentAllocationBand'
          }
        ];
      }

      seriesArray.push({
        type: 'scatter',
        id: 'currentAllocationText',
        marker: {
          enabled: true,
          radius: 0
        },
        data: [[currentMarketShare > 9 ? 9 : currentMarketShare, 0]],
        dataLabels: {
          enabled: false,
          verticalAlign: 'top',
          color: '#4ac6eb',
          y: 0,
          format: `${Math.ceil(currentMarketShare * 10)}`,
          useHTML: true,
          crop: false,
          overflow: 'none'
        }
      });
    }

    return $.extend(
      true,
      {},
      pricingChartSmallDefaults,
      { series: seriesArray },
      benchmarkAxisOverride,
      axisOverride,
      {
        microChartData
      },
      {
        sourceData: {
          targetMarketShare: targetMarketShare * 10
        }
      }
    );
  }

  /**
   * This was formerly in the pricingGraphModal.js and there is still a function there which calls this one so the
   * rest of the code there doesn't have to change. This has been extracted though because I'm hoping to use it
   * multiple places.
   *
   * @param componentData
   * @param graphData
   * @param shimAmounts
   * @param disableHover
   * @returns {
   *   {
   *     chart: {
   *       events: {}
   *     },
   *     tooltip: {
   *       animation: boolean,
   *       shared: boolean,
   *       borderRadius: number,
   *       borderWidth: number,
   *       backgroundColor: string,
   *       useHTML: boolean,
   *       followPointer: boolean,
   *       positioner: positioner,
   *       formatter: formatter
   *     },
   *     xAxis: {
   *       plotLines: Array,
   *       plotBands: {}
   *     },
   *     yAxis: {
   *       plotLines: Array,
   *       plotBands: {}
   *     },
   *     plotOptions: {
   *       series: {
   *         cursor: string
   *       }
   *     }
   *   }
   * }
   */

  // This is named badly. It implies that it creates the microchart highcharts config -- in reality, it only creates the
  // large version.
  // We can create the small version without even calling this.
  createGraphData(
    componentData,
    graphData,
    shimAmounts,
    backupTargetMarketShare
  ) {
    // can probably keep this in the other service
    var targetMarketShare = backupTargetMarketShare;
    var underAllocated = true;

    let bigGraphInfo = angular.copy(this.bigGraphInfo);
    bigGraphInfo.series = angular.copy(graphData.series);

    // Swap out smaller markers in the graph for larger ones.
    $.each(bigGraphInfo.series, (index, val) => {
      if (
        bigGraphInfo.series[index].marker &&
        bigGraphInfo.series[index].marker.symbol ==
          'url(/aptitude/images/bestPriceMarker16x16.png)'
      ) {
        bigGraphInfo.series[index].marker.symbol =
          'url(/aptitude/images/bestPriceMarker40x40.png)';
      }

      if (
        bigGraphInfo.series[index].marker &&
        bigGraphInfo.series[index].marker.symbol ==
          'url(/aptitude/images/bestPriceMarker_loss16x16.png)'
      ) {
        bigGraphInfo.series[index].marker.symbol =
          'url(/aptitude/images/bestPriceMarker_loss40x40.png)';
      }

      if (
        bigGraphInfo.series[index].marker &&
        bigGraphInfo.series[index].marker.symbol ==
          'url(/aptitude/images/exactPriceMarkerSmall.png)'
      ) {
        // While we're here, grab the targetMarketShare
        targetMarketShare = bigGraphInfo.series[index].data[0][0] * 10;
        bigGraphInfo.series[index].marker.symbol =
          'url(/aptitude/images/exactPriceMarker.png)';
        bigGraphInfo.series[index].zIndex = 25;
        bigGraphInfo.series[index].enableMouseTracking = false;
      }
    });

    // This query is more complicated than it needs to be and the reason is that the data structure on the Purchase
    // Planning page has an allocation section which is not present for the same data structure on the Offer Impact
    // page. Both are coming from the back-end but they lack equivalence. This code smooths over that and uses
    // the right number in both cases.
    if (
      typeof componentData.marketShare !== 'undefined' &&
      typeof targetMarketShare !== 'undefined' &&
      ((componentData.allocation != null &&
        componentData.allocation.marketShare >= targetMarketShare) ||
        (componentData.allocation == null &&
          componentData.marketShare >= targetMarketShare))
    ) {
      // Green (isn't everything great) color
      underAllocated = false;
    } else {
      // Orange (underperforming) color
      underAllocated = true;
    }

    // These are fallbacks, we're resetting later based on yAxisRange.
    bigGraphInfo.yAxis.max = graphData.yAxis.max;
    bigGraphInfo.yAxis.min = graphData.yAxis.min;
    var yAxisRange = 11;

    // We pulled all the series data over wholesale from the other chartServices function results
    // Now we look for info to help set the Y axis range

    // Set range based on data extremes
    $.each(bigGraphInfo.series, (index, value) => {
      let i;

      if (value.id == 'hiSeries') {
        for (i = 0; i < value.data.length; i++) {
          if (value.data[i].high > yAxisRange) {
            yAxisRange = Math.ceil(value.data[i].high * 1.05);
          }
        }
      }

      if (value.id == 'loSeries') {
        for (i = 0; i < value.data.length; i++) {
          if (Math.abs(value.data[i].low) > yAxisRange) {
            yAxisRange = Math.ceil(Math.abs(value.data[i].low) * 1.05);
          }
        }
      }

      if (value.id == 'exactPrice') {
        for (i = 0; i < value.data.length; i++) {
          if (Math.abs(value.data[0][1]) > yAxisRange) {
            yAxisRange = Math.ceil(Math.abs(value.data[0][1]) * 1.05);
          }
        }
      }
    });

    if (graphData.xAxis.plotLines) {
      bigGraphInfo.xAxis.plotLines[0] = {
        value:
          graphData.xAxis.plotLines[0].value > 9.4 &&
          graphData.xAxis.plotLines[0].value < 9.9
            ? 9.4
            : graphData.xAxis.plotLines[0].value,
        id: 'currentAllocation',
        color: '#4ac6eb',
        width: 2,
        zIndex: 3
      };

      bigGraphInfo.xAxis.plotBands = {
        color: this.allocationColor(underAllocated),
        from: -1,
        to:
          graphData.xAxis.plotLines[0].value > 9.4 &&
          graphData.xAxis.plotLines[0].value < 9.9
            ? 9.4
            : graphData.xAxis.plotLines[0].value,
        id: 'currentAllocationBand'
      };
    }

    bigGraphInfo.yAxis.max = yAxisRange * 1.1;
    bigGraphInfo.yAxis.min = yAxisRange * -1.1;

    return bigGraphInfo;
  }

  /**
   * Poor form to query the DOM from here, but the alternatives are all quite cumbersome for this simple task.
   * Each rendering engine produces slightly different results with the same data passed to the Highcharts.renderer method.
   * Seems to be no alternative but to browser-sniff.
   *
   * @type {[type]}
   */
  private pixelShimCalculator() {
    var $html = $('html');

    if ($html.hasClass('gecko')) {
      if ($html.hasClass('win')) {
        return { pixelShimWidth: 3, pixelShimHeight: 0 };
      } else {
        return { pixelShimWidth: 2, pixelShimHeight: 0 };
      }
    }

    return { pixelShimWidth: 0, pixelShimHeight: 0 };
  }

  private allocationColor(underAllocated) {
    return underAllocated ? '#F7E4A9' : '#EEF7E4';
  }
}
